import React, { Fragment, useEffect, useRef, useState, useMemo } from "react";
import { Menu } from "@headlessui/react";
import { twClassNames } from "../../helpers/twClassNames";
import { debounce } from "lodash";
import Fuse from "fuse.js";
import type { Workspace, WorkspaceUsage } from "../../../../state/types";
import { workspace as workspaceUtils } from "@rflocal/shared";
import TextInput from "../TextInput";

function getWorkspaceMembersCountText(members: Record<string, any> | undefined) {
    if (!members) return "0 Members";
    const membersCount =
        Object.keys(workspaceUtils.members.getMembersCountingTowardUsageLimitMap(members)).length ||
        1;
    return `${membersCount} Member${membersCount > 1 || membersCount === 0 ? "s" : ""}`;
}

export type BaseWorkspaceSwitcherProps = {
    user: { name: string; email: string; profilePhoto?: string };
    workspaces: { [id: string]: any };
    workspaceUsages?: { [id: string]: any };
    currentWorkspace?: any;
    onWorkspaceSelect?: (workspaceId: string) => void;
    onItemClick?: () => void;
    isMenu?: boolean;
    searchThreshold?: number;
    className?: string;
    createNewWorkspace?: () => void;
};

const WorkspaceItem = React.forwardRef<
    HTMLAnchorElement,
    {
        workspace: Workspace;
        usage: WorkspaceUsage;
        handleWorkspaceClick: (workspaceId: string) => void;
        isSelected: boolean;
    }
>(({ workspace, usage, handleWorkspaceClick, isSelected }, ref) => {
    const icon = workspaceUtils.plan.getIconForPlan(usage?.plan);
    const title = workspaceUtils.plan.getTitleForPlan(usage?.plan);

    return (
        <div className="relative">
            <a
                className={twClassNames("workspaceItem", isSelected && "selected")}
                href="#"
                onClick={(e) => {
                    e.preventDefault();
                    handleWorkspaceClick(workspace.id);
                }}
                ref={isSelected ? ref : null}
            >
                <div className="workspaceInfo max-w-full pr-12">
                    <span className="workspaceName">{workspace.name}</span>
                    <div className="workspaceMetadata">
                        {(icon || title) && (
                            <div className="workspacePlan">
                                <i className={twClassNames(icon, "planIcon")}></i>
                                <span>{title}</span>
                            </div>
                        )}
                        <div className="workspaceMembers">
                            <i className="far fa-users"></i>
                            <span>{getWorkspaceMembersCountText(workspace.members)}</span>
                        </div>
                    </div>
                </div>
            </a>
        </div>
    );
});

export function BaseWorkspaceSwitcher({
    user,
    workspaces,
    workspaceUsages,
    currentWorkspace,
    onWorkspaceSelect,
    onItemClick,
    isMenu = false,
    searchThreshold = 5,
    className = "",
    createNewWorkspace = null
}: BaseWorkspaceSwitcherProps) {
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredWorkspaces, setFilteredWorkspaces] = useState<string[]>([]);
    const currentWorkspaceRef = useRef<HTMLAnchorElement>(null);

    // Convert workspaces object to array for Fuse.js
    const workspacesArray = useMemo(() => {
        return Object.entries(workspaces).map(([id, workspace]) => ({ id, ...workspace }));
    }, [workspaces]);

    const workspacesFuse = useMemo(
        () => new Fuse(workspacesArray, { keys: ["name"], threshold: 0.3 }),
        [workspacesArray]
    );

    // Create memoized debounced search function
    const debouncedSearch = useMemo(
        () =>
            debounce((term: string) => {
                if (term) {
                    const results = workspacesFuse.search(term);
                    setFilteredWorkspaces(results.map((result) => result.item.id));
                } else {
                    setFilteredWorkspaces(Object.keys(workspaces));
                }
            }, 300),
        [workspaces, workspacesFuse]
    );

    // Use debounced search in effect
    useEffect(() => {
        debouncedSearch(searchTerm);

        // Cleanup
        return () => {
            debouncedSearch.cancel();
        };
    }, [searchTerm, debouncedSearch]);

    useEffect(() => {
        if (currentWorkspaceRef.current) {
            currentWorkspaceRef.current.scrollIntoView({
                behavior: "instant",
                block: "center",
                inline: "center"
            });
        }
    }, [currentWorkspaceRef.current]);

    const handleWorkspaceClick = (workspaceId: string) => {
        if (onWorkspaceSelect) {
            onWorkspaceSelect(workspaceId);
        }
        if (onItemClick) {
            onItemClick();
        }
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === " ") {
            e.stopPropagation();
        }
        if (e.key === "Enter" && filteredWorkspaces.length > 0 && searchTerm) {
            handleWorkspaceClick(filteredWorkspaces[0]);
        }
    };

    const ItemComponent = isMenu ? Menu.Item : Fragment;

    return (
        <div className={className}>
            {/* Header and Search - Sticky Container */}
            <div className="sticky top-0 z-10 bg-white">
                {/* Header */}
                <div className="flex w-full cursor-default flex-row items-center justify-between gap-2 p-1">
                    <span className="flex h-8 flex-none items-center pl-3 text-xs font-medium text-gray-600">
                        Your Workspaces
                    </span>
                    {createNewWorkspace && (
                        <button onClick={createNewWorkspace} className="btn2 small ghost square">
                            <i className="far fa-plus text-xs"></i>
                        </button>
                    )}
                </div>

                {/* Search input */}
                {Object.keys(workspaces).length > searchThreshold && (
                    <div className="px-3 pb-2">
                        <TextInput
                            placeholder="Search by name"
                            value={searchTerm}
                            onChange={(value) => setSearchTerm(value)}
                            onKeyDown={handleSearchKeyDown}
                            icon="far fa-search"
                            size="xsmall"
                            fullWidth
                        />
                    </div>
                )}
            </div>

            {/* Workspace list */}
            {filteredWorkspaces.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 px-4 py-8">
                    <i className="fa-solid fa-empty-set text-gray-400"></i>
                    <p className="text-sm font-medium text-gray-700">No workspaces found</p>
                </div>
            ) : (
                filteredWorkspaces
                    .sort((aId, bId) => {
                        const aName = workspaces[aId]?.name || "";
                        const bName = workspaces[bId]?.name || "";
                        return aName.localeCompare(bName);
                    })
                    .map((wId) => {
                        const workspace = workspaces[wId];
                        const isSelected = workspace.id === currentWorkspace?.id;
                        return (
                            <ItemComponent key={wId}>
                                <WorkspaceItem
                                    workspace={workspace}
                                    usage={workspaceUsages?.[wId]}
                                    handleWorkspaceClick={handleWorkspaceClick}
                                    isSelected={isSelected}
                                    ref={currentWorkspaceRef}
                                />
                            </ItemComponent>
                        );
                    })
            )}
        </div>
    );
}
